/** Every discriminator the backend can emit on a v2 attendance payload. Data kinds carry a
 *  populated `data` object; meta kinds (the last block) carry `data: null` and drive control-flow
 *  views. Kept in sync with the backend `processQuery` response contract. */
export type AttendanceKind =
  // data kinds
  | 'result'
  | 'status'
  | 'summary'
  | 'class_report'
  | 'class_range_report'
  | 'general_breakdown'
  | 'list'
  | 'count'
  | 'single'
  | 'comparison'
  | 'safety_check'
  | 'trend'
  | 'ranked_students'
  | 'comprehensive_report'
  | 'evidence'
  // container kind: the answer is split across `parts`, each a payload in its own right
  | 'compound'
  // meta kinds (data === null)
  | 'disambiguation'
  | 'explain'
  | 'smalltalk'
  | 'needs_input'
  | 'gap'
  | 'error'

const NON_RESULT_KINDS = new Set<string>([
  'disambiguation',
  'needs_input',
  'error',
  'explain',
  'smalltalk',
  'safety_check',
  'compound',
  'gap',
])

/** Payload kinds that render summary/table results — everything except the control-flow / narrative
 *  kinds (disambiguation prompts, missing-input prompts, errors, explanations, smalltalk, and safety
 *  checks, which have their own views or are narrative-only). The backend introduces new result-ish
 *  kind strings over time, so this is deny-listed rather than allow-listed to avoid silently
 *  dropping unrecognized ones. */
export function isAttendanceResultPayloadKind(kind: AttendanceKind | string): boolean {
  return !NON_RESULT_KINDS.has(kind)
}

/** What one mark in this answer covers: `whole-day` is a single mark per person per day,
 *  `per-period` a mark per subject period. This is measured from the data, unlike the tenant's
 *  `subject_attendance` setting, which describes the school rather than the answer. */
export type AttendanceMarkType = 'whole-day' | 'per-period'

/** `markType` in the portal's own vocabulary, or `undefined` when the payload states none — leave
 *  the absence visible so callers can fall back rather than assert a unit the data never gave. */
export function markTypeToAttendanceMode(markType: unknown): 'day' | 'subject' | undefined {
  if (markType === 'per-period') return 'subject'
  if (markType === 'whole-day') return 'day'
  return undefined
}

/** WHICH BRANCHES AND LEVELS an answer covered, resolved by the backend into the tenant's own
 *  names. The rows were always clamped to the caller's RBAC scope; what was missing was any way to
 *  SAY what the clamp resolved to, so the same five class names read as "best in the school" or
 *  "best on one campus" depending on what the reader assumed.
 *
 *  `narrowedByActiveContext` is the case that actually bit: the portal sends its branch switcher on
 *  every request, so an admin with one campus selected was reading single-campus answers as
 *  school-wide ones with nothing on screen to say otherwise. */
export interface AttendanceScope {
  /** A finished sentence, composed by the backend — render it, do not reassemble it from the parts
   *  below, which exist for follow-up recall rather than for display. */
  label?: string | null
  branches?: string[]
  levels?: string[]
  branchCount?: number
  levelCount?: number
  branchesTruncated?: boolean
  levelsTruncated?: boolean
  /** True when the answer covered everything the caller may see. */
  isFullPermittedScope?: boolean
  permittedBranchCount?: number
  permittedLevelCount?: number
  /** The portal's branch switcher, not the user's words, is why this is a subset. */
  narrowedByActiveContext?: boolean
  /** The user asked for their full scope and the active context was set aside. */
  widenedOnRequest?: boolean
  /** Whether scope changes how this answer reads — false for a named person's own records. */
  disclose?: boolean
}

export type AttendanceAudience = 'student' | 'teacher' | 'staff' | 'class' | 'school'

export type AttendanceIntent =
  | 'individual'
  | 'roster'
  | 'ranking'
  | 'summary'
  | 'compare'
  | 'pattern'
  | 'explain'

export interface QuerySpec {
  audience: AttendanceAudience
  intent: AttendanceIntent
  attendanceMode: 'day' | 'subject'
  rosterFilter?: string | null
  scope: Record<string, unknown>
  dateRange: {from: string | null; to: string | null; source: string | null}
  output: {groupBy?: string | null; sortBy?: string | null; limit?: number}
}

export interface AttendanceSummary {
  present: number
  absent: number
  late: number
  leave: number
  bunk: number
  other: number
  total: number
  /** `null` when the backend suppressed the rate — see `AttendanceStats.percentUnavailableReason`.
   *  Never coerce this to 0: it renders as "0% attendance" beside a full bar of Present marks. */
  presentPercent: number | null
  dateFrom?: string
  dateTo?: string
  personName?: string
  totalStudents?: number
}

/** The status roll-up the backend computes over the returned rows and emits as `data.stats` for any
 *  per-row/roster result (a query with a status column). Field names differ from `AttendanceSummary`
 *  — `unmarked` rather than `other`, `totalMarked`/`totalRows` rather than `total`, `attendancePercent`
 *  rather than `presentPercent`, plus `attended` (present + late). Null for pure-aggregate queries,
 *  which have no per-row status. See `statsToSummary`. */
export interface AttendanceStats {
  present?: number | null
  late?: number | null
  absent?: number | null
  leave?: number | null
  bunk?: number | null
  unmarked?: number | null
  totalMarked?: number | null
  totalRows?: number | null
  checkout?: number | null
  /** present + late — late counted as present. */
  attended?: number | null
  attendancePercent?: number | null
  /** The exact fraction `attendancePercent` was computed from. The denominator is the marks the
   *  backend counted, which is NOT every bucket: Unmarked and Checkout are reportable but sit
   *  outside the rate, so a ring drawn over all of them contradicts the label in its own centre. */
  percentNumerator?: number | null
  percentDenominator?: number | null
  /** The pre-labelled, pre-ordered tally to render, zero buckets already dropped.
   *  `countsTowardRate` says whether `percentDenominator` includes that bucket. */
  buckets?: Array<{
    key: string
    label: string
    value: number
    countsTowardRate?: boolean
  }> | null
  /** Names of the requested items (students/classes) that returned no attendance records at all, so
   *  a partial answer can flag them apart from the rows it did find. Only present — and only
   *  populated — when at least one requested item had nothing. */
  noDataFor?: string[] | null
}

/** One labeled result set inside a fanned-out answer the backend returns under `data.sections` —
 *  e.g. a query split into one set per class or per subject, each carrying its own rows and `stats`.
 *  `error` is set when that particular set failed while the others succeeded, so a partial answer can
 *  show what it has and flag what it couldn't get. */
export interface AttendanceResultSection {
  /** Human label for this set (class name, subject, date…), used as the block heading. */
  label?: string | null
  columns?: string[]
  rows?: AttendanceRow[]
  grouped?: GroupedRow[]
  stats?: AttendanceStats | null
  /** The section's own window and mark unit, in the same shapes as the top-level `data.period` /
   *  `data.markType`. A section exported on its own has no other source for either. */
  period?: {from?: string; to?: string; label?: string; isAllTime?: boolean} | null
  markType?: AttendanceMarkType | null
  error?: string | null
}

export interface AttendanceRow {
  date?: string
  status?: string
  subjectName?: string
  studentName?: string
  className?: string
  section?: string
  absent?: number
  present?: number
  late?: number
  leave?: number
  presentPercent?: number
  warningCount?: number
  [key: string]: string | number | undefined
}

export interface GroupedRow {
  label?: string
  present?: number
  absent?: number
  presentPercent?: number
  [key: string]: string | number | undefined
}

export interface PersonOption {
  userId?: number
  personId?: number
  studentId?: string | number
  name: string
  class?: string
  /** The candidate's grade as the backend labelled it — carried through to the selection so the
   *  backend can match the exact person. `class` mirrors it for display. */
  grade?: string
  section?: string
  branch?: string
  /** Candidate kind (student/teacher/staff) from `data.candidates`; echoed back on selection. */
  type?: string
}

/** A non-person disambiguation choice: a class, a section, a branch — or a subject, which reuses this
 *  shape because an id and a label is all any of them needs to be picked. */
export interface ClassOption {
  classId?: string | number
  className?: string
  gradeName?: string
  section?: string
  branch?: string
  label?: string
  subjectId?: string | number
  /** `class` | `subject` — which id this option carries, so the card can say "Subject ID" rather than
   *  labelling a subject's id as a class's. */
  kind?: string
  /** The class a subject is taught to. Two subjects called "Economics" differ only by this, so a card
   *  without it offers the reader two identical choices. */
  qualifier?: string
  /** The level a grade or section belongs to ("A Level", "General"). Same job as `qualifier` for a
   *  subject: two grades both called "AS" are told apart by their level and campus, nothing else. */
  levelName?: string
  /** What to send when this option is picked, composed by the backend, which is the only side that
   *  knew the ids. The label alone is the string that was ambiguous in the first place, so replying
   *  with it re-asks the question — see `classSubmitQuery`. */
  submitQuery?: string
}

/** A student with no attendance marked for the day, as reported in a `safety_check` payload. */
export interface UnmarkedStudent {
  studentName?: string
  grade?: string
  section?: string
  className?: string
}

/** A classroom with partially/entirely unmarked attendance, as reported in a `safety_check`
 *  payload. */
export interface IncompleteClass {
  classId?: string | number
  className?: string
  gradeName?: string
  sectionName?: string
  enrolled_count?: number
  marked_count?: number
}

/** One roll-up bucket of a `comprehensive_report`. Only the axis key matching the roll-up it came
 *  from is populated (a `rollups.grade` row carries `grade`, a `rollups.section` row `section`…). */
export interface ReportRollupRow {
  grade?: string
  section?: string
  className?: string
  gender?: string
  campus?: string
  student_count?: number
  attendance_percentage?: number
  /** The backend adds roll-up axes over time (`campus` was one), and each row keys its bucket name
   *  by the axis it came from — so readers must be able to look up an axis they don't know. */
  [axis: string]: string | number | undefined
}

export interface ReportComparisonRange {
  from?: string
  to?: string
  attendance_percentage?: number
  marked_days?: number
}

export interface LowestClassRow {
  classId?: string | number
  className?: string
  gradeName?: string
  sectionName?: string
  attendance_percentage?: number
}

export interface BelowThresholdStudentRow {
  userId?: string | number
  studentName?: string
  grade?: string
  section?: string
  attendance_percentage?: number
}

/** The `comprehensive_report` payload body: multi-axis roll-ups plus period comparison and the two
 *  attention lists. None of it lives under the canonical `rows`/`grouped` keys, so the generic
 *  result table can't read it — see `AttendanceV2ComprehensiveReportView`. */
export interface ComprehensiveReportData {
  from?: string
  to?: string
  rollups?: {
    grade?: ReportRollupRow[]
    section?: ReportRollupRow[]
    className?: ReportRollupRow[]
    gender?: ReportRollupRow[]
    campus?: ReportRollupRow[]
    [axis: string]: ReportRollupRow[] | undefined
  }
  comparison?: {
    rangeA?: ReportComparisonRange
    rangeB?: ReportComparisonRange
  }
  lowestClasses?: LowestClassRow[]
  belowThresholdStudents?: BelowThresholdStudentRow[]
  threshold?: number
  belowThreshold?: number
  unavailableAxes?: string[]
  /** The reporting window, pre-formatted by the backend. */
  period?: {from?: string; to?: string; label?: string; isAllTime?: boolean} | null
}

export interface AttendanceV2Payload {
  version: 2
  kind: AttendanceKind
  narrative: string
  querySpec: QuerySpec
  data:
    | ({
        summary?: AttendanceSummary
        /** The number the question actually asked for, with the noun to label it — e.g. `{value: 3,
         *  label: 'days absent'}`. `summary.total` counts every marked day in the range instead, so
         *  the ring must prefer this or it answers a question nobody asked. */
        headline?: {value?: number | null; label?: string | null} | null
        /** Row count of the result set; the fallback headline figure. */
        recordCount?: number | null
        /** The unit behind every mark counted here. The export's "Attendance Mode" line means this
         *  — not the tenant setting it used to read, which says nothing about the answer. */
        markType?: AttendanceMarkType | null
        /** The branches and levels behind these figures. Rendered beside the date range for the
         *  same reason the date range is rendered: without it the numbers are uninterpretable. */
        scope?: AttendanceScope | null
        /** Unfiltered status counts for the same person/range. `summary` only carries the statuses
         *  the query filtered to, leaving the rest null. */
        fullSummary?: Partial<AttendanceSummary> | null
        /** Status roll-up over the returned rows, in the backend's per-row field names. This is the
         *  canonical source for the visual summary on roster/per-row results, where `rows[].status`
         *  is a numeric code the ring can't classify. See `statsToSummary`. */
        stats?: AttendanceStats | null
        /** Labeled result sets for a fanned-out query. When present, each renders as its own result
         *  block with its own stats/error and the top-level `rows`/`stats` go unused. Absent for a
         *  single-result answer, which falls back to `rows`/`stats`. See `getResultSections`. */
        sections?: AttendanceResultSection[] | null
        rows?: AttendanceRow[]
        grouped?: GroupedRow[]
        shape?: string
        date?: string
        unmarkedStudents?: UnmarkedStudent[]
        incompleteClasses?: IncompleteClass[]
        assumptions?: string[]
      } & ComprehensiveReportData)
    | null
  disambiguation: {
    field: 'person' | 'class' | 'branch'
    options: PersonOption[] | ClassOption[]
  } | null
  needsInput: {
    fields: string[]
    message: string
  } | null
  /** Set on `compound` payloads: one entry per sub-request of a multi-part question. Parsing lifts
   *  each raw part into a full payload (see `parseAttendanceV2Answer`) so every reader — table,
   *  chart, export — works on a part exactly as it does on a standalone answer. */
  parts?: CompoundPart[]
  /** The backend's own view of a multi-part answer, sent even when it flattened the parts into a
   *  single merged `data.rows` and reported a data `kind` rather than `compound`. Parsing lifts
   *  `compound.parts` into `parts` in that case, so nothing downstream reads this directly. */
  compound?: {
    mode?: string
    partCount?: number
    /** Indexes of the parts folded into the top-level merged `data`. */
    promotedIndexes?: number[]
    /** Indexes the merge left out — visible only via `parts`, which is why we lift them. */
    unmergedIndexes?: number[]
    parts?: CompoundPart[]
  } | null
  /** Set on `gap` payloads/parts: the capability the backend can't answer, in plain language. */
  gap?: string | null
  /** The sub-request this part answers, echoed back by the backend. `compound` parts only. */
  requestText?: string
  error?: string
}

/** A member of a `compound` answer: a normalized payload plus the sub-request it answers. */
export interface CompoundPart extends AttendanceV2Payload {
  index: number
  requestText?: string
  gap?: string | null
}

/** Parts of a `compound` payload, or an empty list for every other kind. */
export function getCompoundParts(payload: AttendanceV2Payload): CompoundPart[] {
  if (payload.kind !== 'compound') return []
  return (payload.parts ?? []).filter(Boolean)
}

/** The answer's labeled result sets, or an empty list when it isn't sectioned. A section survives
 *  only if it carries something to show — rows, grouped rows, stats, or an error — so an empty stub
 *  never draws a bare heading. */
export function getResultSections(payload: AttendanceV2Payload): AttendanceResultSection[] {
  const sections = payload.data?.sections
  if (!Array.isArray(sections)) return []
  return sections.filter(
    (section): section is AttendanceResultSection =>
      Boolean(section) &&
      (Boolean(section.rows?.length) ||
        Boolean(section.grouped?.length) ||
        Boolean(section.stats) ||
        Boolean(section.error?.trim()))
  )
}

/** True when the answer splits into labeled result sets that must render per-section rather than
 *  from the top-level `rows`/`stats`. */
export function hasResultSections(payload: AttendanceV2Payload): boolean {
  return getResultSections(payload).length > 0
}

/** Returns the report body only when the payload is a `comprehensive_report` that actually carries
 *  renderable sections; `null` otherwise, so callers can fall back to the generic result view. */
export function getComprehensiveReportData(
  payload: AttendanceV2Payload
): ComprehensiveReportData | null {
  if (payload.kind !== 'comprehensive_report' || !payload.data) return null
  const data = payload.data as ComprehensiveReportData

  const hasRollups = Object.values(data.rollups ?? {}).some(
    (rows) => Array.isArray(rows) && rows.length > 0
  )
  const hasLists = Boolean(data.lowestClasses?.length || data.belowThresholdStudents?.length)
  const hasComparison = data.comparison?.rangeA?.attendance_percentage != null

  return hasRollups || hasLists || hasComparison ? data : null
}

/** A report the backend returned with every bucket empty — no roll-up rows, no attention lists, no
 *  comparison. Nothing to table, so the answer is otherwise just a narrative promising a report
 *  that never appears; callers say so explicitly instead. */
export function isEmptyComprehensiveReport(payload: AttendanceV2Payload): boolean {
  if (payload.kind !== 'comprehensive_report') return false
  if (getComprehensiveReportData(payload)) return false
  return !payload.data?.rows?.length
}

/** The reporting window in words, preferring the backend's own phrasing. */
export function getReportPeriodLabel(payload: AttendanceV2Payload): string | null {
  const data = payload.data
  const label = data?.period?.label?.trim()
  if (label) return label
  if (data?.from && data?.to) return `${data.from} – ${data.to}`
  return data?.from || data?.to || null
}

export interface ParsedAttendanceV2Answer {
  narrative: string
  payload: AttendanceV2Payload | null
}
